/**
 * Client-side password rules for the onboarding PasswordCard — the design's
 * `pwCheck` (ob-entry.jsx), unchanged in rules and score.
 *
 * The server is the authority (People's accept endpoint runs Django's
 * MinimumLength(12) + CommonPassword + UserAttributeSimilarity validators and
 * answers `{password: [...]}`); this only drives the live checklist and the
 * strength meter so the user sees the rules before submitting.
 */

export const PW_MIN_LENGTH = 12;

/** The design's short "known breach" list — a hint, not the real list (the server checks Django's ~20k). */
export const PW_BREACHED = ["password", "123456", "qwerty", "welcome", "letmein", "admin123", "p@ssw0rd"] as const;

export interface PwCheckResult {
  /** ≥ min length (12). */
  len: boolean;
  /** Not containing a known-breached fragment (false while empty). */
  breach: boolean;
  /** Not containing any of the user's name / email parts (false while empty). */
  name: boolean;
  /** All three rules pass. */
  ok: boolean;
  /** Strength 0–4 (Too weak … Strong). Capped at 1 while a breach/name rule fails. */
  score: 0 | 1 | 2 | 3 | 4;
}

export function pwCheck(pw: string, nameParts: ReadonlyArray<string | null | undefined>, minLength = PW_MIN_LENGTH): PwCheckResult {
  const low = pw.toLowerCase();
  const len = pw.length >= minLength;
  const breach = pw.length > 0 && !PW_BREACHED.some((b) => low.includes(b));
  const name = pw.length > 0 && !nameParts.some((n) => !!n && low.includes(n.toLowerCase()));
  let score = 0;
  if (pw.length >= 8) score++;
  if (pw.length >= 12) score++;
  if (/[a-z]/.test(pw) && /[A-Z]/.test(pw) && /\d/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw) && pw.length >= 14) score++;
  if (!breach || !name) score = Math.min(score, 1);
  return { len, breach, name, ok: len && breach && name, score: score as PwCheckResult["score"] };
}

/**
 * Derive the "name or email" parts a password must not contain: each word of
 * the person's name, the email's local-part pieces and the first label of its
 * domain (`sara.al-mansouri@nahdagroup.com` → sara, mansouri, nahdagroup).
 * Parts shorter than 3 characters are dropped — "al" would reject half of all
 * passwords without making any of them safer.
 */
export function pwNameParts(...sources: Array<string | null | undefined>): string[] {
  const parts = new Set<string>();
  for (const src of sources) {
    if (!src) continue;
    const s = src.trim().toLowerCase();
    if (!s) continue;
    const [local, domain] = s.includes("@") ? s.split("@", 2) : [s, ""];
    for (const piece of local.split(/[\s._+\-]+/)) if (piece.length >= 3) parts.add(piece);
    const firstLabel = domain.split(".")[0];
    if (firstLabel && firstLabel.length >= 3) parts.add(firstLabel);
  }
  return [...parts];
}
